import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Ban, RotateCcw } from 'lucide-react'
import { useMemo } from 'react'

import { cancelJob, retryJob, type JobLane, type JobStatus } from '@/api/jobs'
import { keys } from '@/api/keys'
import { JobModelLabel } from '@/components/JobModelLabel'
import { Badge, type BadgeTone } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { toast } from '@/components/ui/toast'
import { cn } from '@/components/ui/utils'
import { formatClock, formatRelative, formatUsd, humanizeJobType } from '@/lib/format'
import type { Job } from '@/schema'
import { useQueueStore } from '@/stores/queueStore'

/**
 * The expanded jobs list rendered from `queueStore` (the last 200 jobs,
 * newest first). Used inside the bottom strip (`compact`) and as the body of
 * `/jobs`. Phase 4 adds the per-job envelope inspector.
 */

export interface QueuePanelProps {
  compact?: boolean
  filters?: { status?: JobStatus; type?: string; lane?: JobLane }
}

const STATUS_TONE: Record<JobStatus, BadgeTone> = {
  queued: 'neutral',
  running: 'accent',
  succeeded: 'ok',
  failed: 'danger',
  cancelled: 'warn',
}

export function QueuePanel({ compact = false, filters }: QueuePanelProps) {
  const jobs = useQueueStore((s) => s.jobs)
  const list = useMemo(
    () => jobs.filter((j) => (!filters?.status || j.status === filters.status) && (!filters?.type || j.type === filters.type) && (!filters?.lane || j.lane === filters.lane)),
    [jobs, filters?.status, filters?.type, filters?.lane],
  )

  const body = list.length === 0 ? <EmptyState filtered={Boolean(filters?.status || filters?.type || filters?.lane)} /> : <ul className="flex flex-col gap-1.5 p-2">{list.map((j) => <JobRow key={j.id} job={j} compact={compact} />)}</ul>

  if (compact) {
    return (
      <ScrollArea id="queue-panel" className="h-full">
        {body}
      </ScrollArea>
    )
  }
  return (
    <section id="queue-panel" className="card overflow-hidden">
      {body}
    </section>
  )
}

function EmptyState({ filtered }: { filtered: boolean }) {
  return (
    <div className="flex flex-col items-center gap-1 px-4 py-10 text-center">
      <p className="text-sm font-medium text-ink">{filtered ? 'No jobs match these filters' : 'No jobs yet'}</p>
      <p className="text-xs text-ink-3">{filtered ? 'Clear a filter to see more.' : 'Index the bucket from Home to get started.'}</p>
    </div>
  )
}

function JobRow({ job, compact }: { job: Job; compact: boolean }) {
  const qc = useQueryClient()
  const upsert = useQueueStore((s) => s.upsertJob)
  const done = () => void qc.invalidateQueries({ queryKey: keys.jobs.all() })

  const cancel = useMutation({
    mutationFn: () => cancelJob(job.id),
    onSuccess: (j) => {
      upsert(j)
      done()
      toast.info(`${j.status === 'cancelled' ? 'Cancelled' : j.cancel_requested ? 'Cancelling' : 'Already finished'} #${job.id}`)
    },
    onError: (e) => toast.error('Could not cancel', { description: e instanceof Error ? e.message : String(e) }),
  })
  const retry = useMutation({
    mutationFn: () => retryJob(job.id),
    onSuccess: (j) => {
      upsert(j)
      done()
      toast.success(`Re-queued #${job.id}`)
    },
    onError: (e) => toast.error('Could not retry', { description: e instanceof Error ? e.message : String(e) }),
  })

  const pct = job.progress?.pct
  const phase = job.progress?.msg ?? job.progress?.phase ?? null
  const pausedFor = job.progress?.data?.paused_for
  const pausedUntil = job.progress?.data?.paused_until
  const cost = job.cost_usd ?? job.progress?.data?.cost_usd
  const isActive = job.status === 'queued' || job.status === 'running'
  const cancelling = isActive && (cancel.isPending || job.cancel_requested)

  return (
    <li className={cn('grid items-center gap-x-3 gap-y-1 rounded-xl px-3 py-2 hover:bg-paper-2', compact ? 'grid-cols-[auto_1fr_auto]' : 'grid-cols-[auto_1fr_auto] sm:grid-cols-[4rem_1fr_auto_auto]')}>
      <span className="font-mono text-xs text-ink-3">#{job.id}</span>
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-ink">{humanizeJobType(job.type)}</span>
          <Badge tone={STATUS_TONE[job.status]} dot>
            {cancelling ? 'cancelling' : job.status}
          </Badge>
          {job.lane.startsWith('ai') ? <Badge tone="info">{job.lane === 'ai_interactive' ? 'story' : 'bulk AI'}</Badge> : null}
          {pausedFor ? (
            <Badge tone="warn">
              {pausedFor === 'interactive' ? 'paused for a story' : pausedFor === 'usage_window' ? `usage limit · ${formatClock(pausedUntil)}` : 'short break'}
            </Badge>
          ) : null}
        </div>
        <JobModelLabel job={job} />
        {cancel.isError ? <p role="alert" className="text-xs text-danger">Could not cancel. {cancel.error.message}</p> : null}
        <div className="mt-0.5 flex items-center gap-2 text-xs text-ink-2">
          {phase ? <span className="truncate" role={cancelling ? 'status' : undefined}>{cancelling ? 'Stopping this job…' : phase}</span> : null}
          {job.error ? (
            <span className="truncate text-danger" title={job.error}>
              {job.error}
            </span>
          ) : null}
          {job.children ? (
            <span className="shrink-0 text-ink-3">
              {job.children.done}/{job.children.total}
              {job.children.failed ? ` · ${job.children.failed} failed` : ''}
            </span>
          ) : null}
        </div>
        {isActive && pct != null ? (
          <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-paper-3" role="progressbar" aria-label={`Job ${job.id} progress`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(pct * 100)}>
            <div className="h-full rounded-full bg-accent transition-[width]" style={{ width: `${Math.round(pct * 100)}%` }} />
          </div>
        ) : null}
      </div>
      {!compact ? (
        <span className="hidden text-right text-xs text-ink-3 sm:block">
          <span className="block">{formatRelative(job.finished_at ?? job.started_at ?? job.created_at)}</span>
          {cost != null ? <span className="block">{formatUsd(cost)}</span> : null}
        </span>
      ) : null}
      <div className="flex items-center gap-1">
        {isActive ? (
          <Button size="sm" variant="ghost" onClick={() => cancel.mutate()} loading={cancelling} disabled={cancelling} aria-label={`${cancelling ? 'Cancelling' : 'Cancel'} job ${job.id}`}>
            <Ban aria-hidden />
            {compact ? null : cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        ) : job.status === 'failed' || job.status === 'cancelled' ? (
          <Button size="sm" variant="ghost" onClick={() => retry.mutate()} loading={retry.isPending} aria-label={`Retry job ${job.id}`}>
            <RotateCcw aria-hidden />
            {compact ? null : 'Retry'}
          </Button>
        ) : null}
      </div>
    </li>
  )
}
